/**
 * @file PortalActionDetail.js
 * @description This page displays the details of a portal action.
 */

import styles from './PortalActions.module.scss';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentType } from '../helpers/getContentType';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { FilterModalV2 } from '../helpers/FilterComponent_V2';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import {
    getAllResponsesByActionId,
    getPortalActionById,
} from '../../services/containerService';
import {
    formatDateToMMDDYYYY,
    formatDateToMmmDDYYYYTimezone,
} from '../helpers/DateUtils';
import AssetDrawer from '../assets/AssetDrawer';
import Toast from '../ui/toasts/Toast';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { processFileName } from '../helpers/FileUtils';
import { fetchAssetByID } from '../../services/clientService';
import { logEvent } from '../../services/loggingService';
import { LogEventType } from '../../utils/data/enums';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const PortalActionDetail = () => {
    const { actionId } = useParams();
    const navigate = useNavigate();

    const [actionDetails, setActionDetails] = useState({});
    const [individuals, setIndividuals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [daysSinceOrToDueDate, setDaysSinceOrToDueDate] = useState(0);
    const pageSize = 10;

    const openModal = column => setActiveModal(column);
    const closeModal = () => setActiveModal(null);

    const handleApply = (column, values) => {
        setSelectedOptions(prev => ({
            ...prev,
            [column]: values,
        }));
    };

    const handleCloseAndRefresh = event => {
        setIsDrawerOpen(event);
        setSelectedAsset(null);
    };

    const clearFilters = () => setSelectedOptions({});

    const loadPortalActionById = async () => {
        const fetchedAction = await getPortalActionById(actionId);
        setActionDetails(fetchedAction);
    };

    const loadResponsesByActionId = async () => {
        const fetchedResponses = await getAllResponsesByActionId(
            actionId,
            pageSize,
            currentPage,
            sortConfig.key,
            sortConfig.direction,
            selectedOptions,
        );
        setIndividuals(fetchedResponses.actionResponses);
        setTotalPages(
            Math.ceil(fetchedResponses.totalActionResponses / pageSize),
        );
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    loadPortalActionById(),
                    loadResponsesByActionId(),
                ]);
            } catch (error) {
                console.error('Failed to fetch data', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
        //TODO: Keep next line for testing asset upload responses in the meantime
        //loadIndividuals();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedOptions]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                await loadResponsesByActionId();
            } catch (error) {
                console.error('Failed to fetch data', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [selectedOptions, currentPage, sortConfig]);

    useEffect(() => {
        if (actionDetails.dueDate) {
            setDaysSinceOrToDueDate(
                calculateDaysSinceDate(actionDetails.dueDate),
            );
        }
    }, [actionDetails]);

    //TODO: Keep for testing asset upload responses in the meantime
    /*const loadIndividuals = async () => {
        setIsLoading(true);
        setIndividuals([
            {
                "id": 0,
                "containerId": 143,
                "firstName": "Dani",
                "lastName": "Choate",
                "uniqueId": "43521ddc463746eebf2848f84d20fe78",
                "contents": null,
                "statusId": 0,
                "completedAt": null
            },
            {
                "id": 5,
                "containerId": 143,
                "lastName": "Brown",
                "firstName": "Chris",
                "uniqueId": "43521ddc463746eebf2848f84d20fe78",
                "statusId": 1,
                "completedAt": null,
                "contents":{"uploadedAssetId":32}
            },
            {
                "id": 1,
                "containerId": 143,
                "lastName": "Doe",
                "firstName": "John",
                "uniqueId": "UUID-1",
                "statusId": 1,
                "completedAt": "2024-10-15T12:30:45Z",
                "contents":{"uploadedAssetId":1}
            }
        ]);
        setIsLoading(false);
    };*/

    const handleSortByColumn = column => {
        setSortConfig(prevConfig => {
            const isCurrentColumn = prevConfig.key === column;
            const newDirection =
                isCurrentColumn &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING;

            return {
                key: column,
                direction: newDirection,
            };
        });
    };

    const renderSortButton = column => {
        const isActive =
            sortConfig.key === column &&
            sortConfig.direction === SortDirections.ASCENDING;
        return (
            <span
                onClick={() => handleSortByColumn(column)}
                className={`px-2 ${styles.cursorPointer}`}
                role="button"
                tabIndex={0}
            >
                <svg width={11} height={6} viewBox="0 0 11 6" fill="none">
                    <path
                        transform={isActive ? 'rotate(180 5.25 3)' : ''}
                        d="M1 0.75L5.25 5.25L9.5 0.75"
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const renderFilterModal = (column, options, transformOptions) => {
        const isSelected =
            selectedOptions[column] && selectedOptions[column].length > 0;
        return (
            <>
                <span
                    onClick={() => openModal(column)}
                    className={styles.cursorPointer}
                >
                    {isSelected ? (
                        <svg
                            style={{
                                marginTop: '4px',
                                display: 'inline-block',
                                width: '1em',
                                height: '1em',
                            }}
                        >
                            <path
                                d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z"
                                fill="#1366FF"
                                stroke="#1366FF"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <i
                            className="bi bi-funnel"
                            style={{ fontSize: '0.7em' }}
                        ></i>
                    )}
                </span>
                {activeModal === column && (
                    <FilterModalV2
                        column={column}
                        options={Array.from(new Set(options))}
                        selectedOptions={selectedOptions[column] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                        transformOptions={transformOptions}
                    />
                )}
            </>
        );
    };

    const handleClickClose = () => navigate(ROUTES.ACTIONS);

    const calculateDaysSinceDate = dateString => {
        const inputDate = new Date(dateString);
        const currentDate = new Date();
        const differenceInMilliseconds = currentDate - inputDate;
        const millisecondsInADay = 1000 * 60 * 60 * 24;
        return Math.floor(differenceInMilliseconds / millisecondsInADay);
    };

    const returnDueDateLabel = dateString => {
        const days = calculateDaysSinceDate(dateString);
        if (days === 0) return 'Today';

        const absoluteDays = Math.abs(days);
        let label = `${absoluteDays} Day${absoluteDays === 1 ? '' : 's'}`;
        return days < 0 ? label : label;
    };

    const getStatusString = status => {
        switch (status) {
            case 0:
                return 'Incomplete';
            case 1:
                return 'Completed';
            default:
                return 'Undefined';
        }
    };

    const getStatusClass = status => {
        switch (status) {
            case 0:
                return 'ing-badge-inactive';
            case 1:
                return 'ing-badge-active';
            default:
                return 'ing-badge-inactive';
        }
    };

    const generateFileName = (baseName = 'data') => {
        const now = new Date();
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = now.toLocaleString('en-US', options);
        return `${baseName}_${`Actions_Report`}_${formattedDate}`
            .replace(/[/]/g, '-')
            .replace(/[:,]/g, '-')
            .replace(/\s/g, '_');
    };

    const formatDateForCSVReport = dateString => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        };
        return date
            .toLocaleString('en-US', options)
            .replace(/,/, '')
            .replace(/\//g, '-')
            .replace(/ /g, ' ');
    };

    const processDataForDownload = data => {
        return data.map(item => ({
            lastName: item.lastName,
            firstName: item.firstName,
            uniqueId: item.uniqueId,
            status: item.statusId === 1 ? 'Completed' : 'Incomplete',
            completedAt: formatDateForCSVReport(item.completedAt),
        }));
    };

    const convertJSONToCSV = (jsonPayload, customHeaders) => {
        const headers = customHeaders
            ? customHeaders.join(',')
            : Object.keys(jsonPayload[0]).join(',');
        const rows = jsonPayload.map(row => Object.values(row).join(','));
        return [headers, ...rows].join('\n');
    };

    const downloadCSV = async () => {
        const customHeaders = [
            'Last Name',
            'First Name',
            'Unique ID',
            'Status',
            'Completed',
        ];
        const filename = generateFileName(actionDetails.containerTitle);
        const individualsWithoutPages = await getAllResponsesByActionId(
            actionId,
            2147483647,
            1,
            sortConfig.key,
            sortConfig.direction,
            selectedOptions,
        );
        const jsonPayload = processDataForDownload(
            individualsWithoutPages.actionResponses,
        );

        const csv = convertJSONToCSV(jsonPayload, customHeaders);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            logEvent(
                LogEventType.DataDownloaded,
                `Data for portal action ${actionDetails.containerTitle} downloaded`,
            );
        }
    };

    const downloadAssets = async () => {
        const zip = new JSZip();
        let filesAdded = false; // Flag to track if any files were added to the zip

        try {
            // Create an array of promises for each response
            const downloadPromises = individuals.map(async individual => {
                const assetId = individual.contents;

                if (assetId !== false && assetId !== null) {
                    // Call fetchAssetByID() and wait for it to complete
                    const fetchedAsset = await fetchAssetByID(assetId, null);

                    // Extract the URL from the fetched asset
                    const url = fetchedAsset.url; // Ensure fetchedAsset has a url property

                    // Fetch the file from the URL
                    const fileResponse = await fetch(url);
                    if (!fileResponse.ok) {
                        throw new Error(`Failed to download file from ${url}`);
                    }

                    const blob = await fileResponse.blob();
                    // const fileName = url.split('/').pop(); // Extract file name from URL
                    const fileName = processFileName(
                        fetchedAsset.name,
                        fetchedAsset.fileType,
                    );

                    // Add the file to the zip
                    zip.file(fileName, blob);
                    filesAdded = true; // Set the flag to true since a file was added
                }
            });

            // Wait for all download promises to complete
            await Promise.all(downloadPromises);

            // Check if any files were added to the zip
            if (!filesAdded) {
                Toast.error(getText('msg_2e41d12cd163'));
                return; // Exit the function early
            }

            // Generate the zip file
            const content = await zip.generateAsync({ type: 'blob' });

            const now = new Date();
            const dateOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };

            const formattedDate = now
                .toLocaleString('en-US', dateOptions)
                .replace(/\//g, '-');

            const folderName = (
                actionDetails.containerTitle +
                '_' +
                formattedDate
            )
                .replace(/\s*-\s*/g, '-')
                .replace(/[\s,\.]+/g, '_')
                .replace(/[<>:"/\\|?*]/g, '')
                .replace(/[^a-zA-Z0-9_\-()]/g, '')
                .trim();

            // Save the zip file
            saveAs(content, folderName + '.zip');
            Toast.success(
                "All assets uploaded have been downloaded into '" +
                    folderName +
                    ".zip'",
            );
            logEvent(
                LogEventType.DataDownloaded,
                `Assets for portal action ${actionDetails.containerTitle} downloaded`,
            );
        } catch (error) {
            console.error(
                'An error occurred during the download process:',
                error,
            );
            Toast.error(getText('msg_18ae3cc0f678'));
        }
    };

    useEffect(() => {
        if (selectedAsset) {
            setIsDrawerOpen(true);
        }
    }, [selectedAsset]);

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div
            className="page-content"
            style={{ height: '100%', overflowY: 'auto' }}
        >
            <div className="card" style={{ minHeight: '100%' }}>
                <div
                    className="card-body px-0 py-4 d-flex flex-column"
                    style={{ minHeight: '100%', overflowX: 'auto' }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-3">
                        <div
                            className="float-start d-flex"
                            style={{ alignItems: 'center' }}
                        >
                            <p className="page-title card-title ms-4 me-5">
                                {actionDetails.containerTitle}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            {actionDetails.containerTypeId === 12 && (
                                <span
                                    type="button"
                                    onClick={() => downloadAssets()}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-cloud-download me-2" />
                                    {getText('msg_20e338624cee')}
                                </span>
                            )}
                            <span
                                type="button"
                                onClick={() => downloadCSV()}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_ee45c30326b7')}
                            </span>
                            <span
                                type="button"
                                onClick={handleClickClose}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>
                    <div className={styles.container}>
                        <div className={styles.status}>
                            {daysSinceOrToDueDate <= 0 ||
                            !actionDetails.dueDate ? (
                                <>
                                    <div
                                        className={`${styles.statusIcon} ${styles.dueIn}`}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="21"
                                            viewBox="0 0 20 21"
                                            fill="none"
                                        >
                                            <path
                                                d="M13.5669 9.20894C13.811 9.45302 13.811 9.84874 13.5669 10.0928L9.81694 13.8428C9.69973 13.96 9.54076 14.0259 9.375 14.0259C9.20924 14.0259 9.05027 13.96 8.93306 13.8428L7.05806 11.9678C6.81398 11.7237 6.81398 11.328 7.05806 11.0839C7.30214 10.8399 7.69786 10.8399 7.94194 11.0839L9.375 12.517L12.6831 9.20894C12.9271 8.96486 13.3229 8.96486 13.5669 9.20894Z"
                                                fill="#1366FF"
                                            />
                                            <path
                                                d="M4.375 0.275879C4.72018 0.275879 5 0.555701 5 0.900879V1.52588H15V0.900879C15 0.555701 15.2798 0.275879 15.625 0.275879C15.9702 0.275879 16.25 0.555701 16.25 0.900879V1.52588H17.5C18.8807 1.52588 20 2.64517 20 4.02588V17.7759C20 19.1566 18.8807 20.2759 17.5 20.2759H2.5C1.11929 20.2759 0 19.1566 0 17.7759V4.02588C0 2.64517 1.11929 1.52588 2.5 1.52588H3.75V0.900879C3.75 0.555701 4.02982 0.275879 4.375 0.275879ZM1.25 5.27588V17.7759C1.25 18.4662 1.80964 19.0259 2.5 19.0259H17.5C18.1904 19.0259 18.75 18.4662 18.75 17.7759V5.27588H1.25Z"
                                                fill="#1366FF"
                                            />
                                        </svg>
                                    </div>
                                    <div className={styles.statusItem}>
                                        <span className={styles.statusTitle}>
                                            {daysSinceOrToDueDate === 0
                                                ? 'Due'
                                                : 'Due In'}
                                        </span>
                                        <span className={styles.statusSubtitle}>
                                            {!actionDetails.dueDate
                                                ? '-'
                                                : returnDueDateLabel(
                                                      actionDetails.dueDate,
                                                  )}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div
                                        className={`${styles.statusIcon} ${styles.pastDue}`}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="5"
                                            height="20"
                                            viewBox="0 0 5 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M0 17.5C0 16.1193 1.11929 15 2.5 15C3.88071 15 5 16.1193 5 17.5C5 18.8807 3.88071 20 2.5 20C1.11929 20 0 18.8807 0 17.5Z"
                                                fill="#EF4E45"
                                            />
                                            <path
                                                d="M0.244907 2.4876C0.111718 1.15571 1.15762 0 2.49615 0C3.83467 0 4.88058 1.15571 4.74739 2.48759L3.87053 11.2562C3.79992 11.9623 3.20576 12.5 2.49615 12.5C1.78654 12.5 1.19238 11.9623 1.12177 11.2562L0.244907 2.4876Z"
                                                fill="#EF4E45"
                                            />
                                        </svg>
                                    </div>
                                    <div className={styles.statusItem}>
                                        <span className={styles.statusTitle}>
                                            {getText('msg_7c15f3b797eb')}
                                        </span>
                                        <span className={styles.statusSubtitle}>
                                            {returnDueDateLabel(
                                                actionDetails.dueDate,
                                            )}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div
                                className={`${styles.statusIcon} ${styles.completed}`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="20"
                                    viewBox="0 0 26 20"
                                    fill="none"
                                >
                                    <path
                                        d="M21.6529 0.675906C22.5541 -0.225302 24.0152 -0.225302 24.9164 0.675906C25.8066 1.56606 25.8175 3.00251 24.9492 3.90607L12.6655 19.2607C12.6478 19.2829 12.6288 19.304 12.6088 19.3241C11.7075 20.2253 10.2464 20.2253 9.34519 19.3241L1.20227 11.1812C0.301065 10.28 0.301065 8.81882 1.20227 7.91761C2.10348 7.0164 3.56463 7.0164 4.46584 7.91761L10.9074 14.3592L21.5917 0.744937C21.6107 0.720723 21.6311 0.697671 21.6529 0.675906Z"
                                        fill="#14BA6D"
                                    />
                                </svg>
                            </div>
                            <div className={styles.statusItem}>
                                <span className={styles.statusTitle}>
                                    {getText('msg_1798b3ba42ee')}
                                </span>
                                <span className={styles.statusSubtitle}>
                                    {actionDetails?.stats?.completedIndividuals}
                                </span>
                            </div>
                            <div
                                className={`${styles.statusIcon} ${styles.incomplete}`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="23"
                                    height="5"
                                    viewBox="0 0 23 5"
                                    fill="none"
                                >
                                    <path
                                        d="M0.0976563 2.5C0.0976563 1.39543 0.737249 0.5 1.52623 0.5L21.5262 0.5C22.3152 0.5 22.9548 1.39543 22.9548 2.5C22.9548 3.60457 22.3152 4.5 21.5262 4.5L1.52623 4.5C0.737249 4.5 0.0976563 3.60457 0.0976563 2.5Z"
                                        fill="#667085"
                                    />
                                </svg>
                            </div>
                            <div className={styles.statusItem}>
                                <span className={styles.statusTitle}>
                                    {getText('msg_387fd1bbc184')}
                                </span>
                                <span className={styles.statusSubtitle}>
                                    {actionDetails?.stats?.totalIndividuals -
                                        actionDetails?.stats
                                            ?.completedIndividuals}
                                </span>
                            </div>
                        </div>
                        <div className={styles.details}>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_b55223c7bdc7')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {actionDetails.portal?.name}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_863ed7dbce40')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {getContentType(
                                        actionDetails.containerTypeId,
                                    )}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_a1b308ec704a')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {formatDateToMMDDYYYY(
                                        actionDetails.dueDate,
                                    )}
                                </span>
                            </div>
                            <div
                                className={`${styles.detailItem} ${styles.detailItemCentered}`}
                            >
                                <span className={styles.detailTitle}>
                                    {getText('msg_63051d96a2df')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {actionDetails.acknowledgmentTypeId === 1
                                        ? 'Y'
                                        : '-'}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_43de2bcd6337')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {actionDetails.createdBy}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_ec4135959bb4')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {formatDateToMMDDYYYY(
                                        actionDetails.createdAt,
                                    )}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_6180d5db3ed9')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {actionDetails.updatedBy || '-'}
                                </span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailTitle}>
                                    {getText('msg_7361e30e7f3b')}
                                </span>
                                <span className={styles.detailSubtitle}>
                                    {formatDateToMMDDYYYY(
                                        actionDetails.updatedAt,
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <div className={styles.tableContainer}>
                            <table
                                className={`table table-hover align-middle ${styles.tableDiv}`}
                            >
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th className={styles.tableHeader}>
                                            <span>
                                                {getText('msg_863cb39fbe7d')}
                                            </span>
                                            {renderSortButton('Last Name')}
                                            {renderFilterModal(
                                                'lastNames',
                                                individuals.map(
                                                    item => item.lastName,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span>
                                                {getText('msg_b6ea992aab46')}
                                            </span>
                                            {renderSortButton('First Name')}
                                            {renderFilterModal(
                                                'firstNames',
                                                individuals.map(
                                                    item => item.firstName,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span>
                                                {getText('msg_7aba334bb9bf')}
                                            </span>
                                            {renderSortButton('Unique ID')}
                                            {renderFilterModal(
                                                'uniqueIds',
                                                individuals.map(
                                                    item => item.uniqueId,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span>
                                                {getText('msg_bae7d5be7082')}
                                            </span>
                                            {renderSortButton('Status')}
                                            {renderFilterModal(
                                                'statusIds',
                                                individuals.map(
                                                    item => item.statusId,
                                                ),
                                                value => getStatusString(value),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span>
                                                {getText('msg_1798b3ba42ee')}
                                            </span>
                                            {renderSortButton('Completed')}
                                            {renderFilterModal(
                                                'completedDates',
                                                individuals.map(
                                                    item => item.completedAt,
                                                ),
                                            )}
                                        </th>
                                        {actionDetails.containerTypeId ===
                                            12 && (
                                            <th
                                                className={`${styles.columnXS} text-center`}
                                            >
                                                <span>
                                                    {getText(
                                                        'msg_69bd4ef9fbd0',
                                                    )}
                                                </span>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {individuals.map(individual => (
                                        <tr key={individual.id}>
                                            <td className={styles.columnBold}>
                                                {individual.lastName}
                                            </td>
                                            <td className={styles.columnBold}>
                                                {individual.firstName}
                                            </td>
                                            <td
                                                className={styles.columnRegular}
                                            >
                                                {individual.uniqueId}
                                            </td>
                                            <td>
                                                <button
                                                    className={getStatusClass(
                                                        individual.statusId,
                                                    )}
                                                >
                                                    {getStatusString(
                                                        individual.statusId,
                                                    )}
                                                </button>
                                            </td>
                                            <td
                                                className={styles.columnRegular}
                                            >
                                                {formatDateToMmmDDYYYYTimezone(
                                                    individual.completedAt,
                                                )}
                                            </td>
                                            {actionDetails.containerTypeId ===
                                                12 && (
                                                <td
                                                    style={{
                                                        width: 'content-fit',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {individual.contents !==
                                                    null ? (
                                                        <span
                                                            className={
                                                                styles.iconView
                                                            }
                                                            onClick={() =>
                                                                setSelectedAsset(
                                                                    individual.contents,
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-eye"></i>
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className={
                                                                styles.columnRegular
                                                            }
                                                        >
                                                            -
                                                        </span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {individuals.length < 1 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={getText('msg_aca14ec90fb5')}
                            description={getText('msg_5328029ad816')}
                        />
                    )}
                    {totalPages > 1 && (
                        <div className="mt-auto">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                    {isDrawerOpen && (
                        <AssetDrawer
                            onClose={e => {
                                setSelectedAsset(null);
                                setIsDrawerOpen(e);
                            }}
                            onSaveOrDelete={handleCloseAndRefresh}
                            assetId={selectedAsset}
                            isPortalActions={true}
                            eventContextDataTypeId={EventDataTypeEnum.Container}
                            eventContextId={actionId}
                            eventContextName={actionDetails.containerTitle}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortalActionDetail;
